import { Delete } from '@mui/icons-material';
import { Autocomplete, Box, Card, CardContent, IconButton, TextField, Typography } from '@mui/material';
import { useViewContractsState } from 'customer-management/customer-details/view-contracts/state/view-contracts-state';
import { useGetDraftContract } from 'shared/queries';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { FormattedNumber, NumberTextField, NumberCompareTextField } from 'shared/controls';
import { ContractRouteParams, HTMLUListElementAttributes, TermForm, TermWithoutAmount } from 'shared/types';
import { getNestedFieldName } from 'shared/utility';
import { combinedTerm } from './view-contracts-contract-terms-container';

const termsSection = getNestedFieldName('terms');

interface ViewContractsContractTermsCardProps {
  index: number;
  field: TermForm;
  remove(): void;
  isRejected: boolean;
  availableOptions: TermWithoutAmount[];
}
export function ViewContractsContractTermsCard({
  index,
  field,
  remove,
  isRejected,
  availableOptions,
}: ViewContractsContractTermsCardProps) {
  const [selectedTerm, setSelectedTerm] = useState<TermWithoutAmount | null>(null);
  const { setValue, register } = useFormContext();
  const state = useViewContractsState();
  const editMode = state.editMode.get();
  const diffingMode = state.diffingMode.get();
  const fieldName = useCallback((field: string) => termsSection(index)(field), [index]);
  useEffect(() => {
    register(fieldName('name'), {
      required: {
        message: 'Term Name is required',
        value: true,
      },
    });
  }, [register, fieldName]);
  useEffect(() => {
    if (selectedTerm) {
      setValue(fieldName('name'), selectedTerm.name);
      setValue(fieldName('bucket'), selectedTerm.bucket);
      setValue(fieldName('display'), selectedTerm.display);
      setValue(fieldName('type'), selectedTerm.type);
    }
  }, [selectedTerm, setValue, fieldName]);

  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {editMode && !diffingMode && (
          <Box sx={{ alignSelf: 'flex-end' }}>
            <IconButton size='small' onClick={remove}>
              <Delete />
            </IconButton>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, alignItems: 'center' }}>
          {field.isNew ? (
            <SelectNameField
              selectedTerm={selectedTerm}
              setSelectedTerm={setSelectedTerm}
              index={index}
              availableOptions={availableOptions}
            />
          ) : (
            <Typography>{field.display}</Typography>
          )}
          <AmountField
            selectedTerm={selectedTerm}
            field={field}
            index={index}
            fieldValue={field.amount}
            isRejected={isRejected}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

interface SelectNameFieldProps {
  index: number;
  selectedTerm: TermWithoutAmount | null;
  setSelectedTerm: Dispatch<SetStateAction<TermWithoutAmount | null>>;
  availableOptions: TermWithoutAmount[];
}

function SelectNameField({ index, selectedTerm, setSelectedTerm, availableOptions }: SelectNameFieldProps) {
  const name = termsSection(index)('name');
  const label = 'Name';
  const { errors } = useFormState();

  const error = errors?.terms?.[index]?.name;

  console.log({ errors, error });

  return (
    <Autocomplete
      value={selectedTerm}
      onChange={(_, value) => {
        setSelectedTerm(value);
      }}
      options={availableOptions}
      getOptionLabel={term => term.display}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      data-testid={`${name}-combobox`}
      ListboxProps={{ 'data-testid': `${name}-listbox` } as HTMLUListElementAttributes}
      renderInput={params => (
        <TextField
          variant='outlined'
          {...params}
          fullWidth
          label={label}
          error={Boolean(error)}
          helperText={error?.message}
        />
      )}
    />
  );
}

interface AmountFieldProps {
  index: number;
  isRejected: boolean;
  field: TermForm | combinedTerm;
  fieldValue?: string | number;
  diffingValue?: string | number;
  selectedTerm: TermWithoutAmount | null;
}
export function AmountField({ fieldValue, field, index, diffingValue, selectedTerm, isRejected }: AmountFieldProps) {
  const { errors } = useFormState();
  const { contractId } = useParams<ContractRouteParams>();
  const { draftContract } = useGetDraftContract(contractId);
  const isDraft = draftContract !== undefined;
  const state = useViewContractsState();
  const editMode = state.editMode.get();
  const diffingMode = state.diffingMode.get();
  const name = termsSection(index)('amount');
  const label = 'Amount';
  const condition = selectedTerm || field;

  const error = errors?.terms?.[index]?.name;
  const helperText = error?.message;
  const rules = {
    required: {
      message: 'Amount is required',
      value: true,
    },
  };
  switch (condition.type) {
    case 'dollar':
      return (editMode && isRejected) || editMode ? (
        <NumberTextField
          name={name}
          label={label}
          prefix='$'
          error={Boolean(error)}
          helperText={helperText}
          rules={rules}
        />
      ) : (diffingMode || isDraft) && !isRejected ? (
        <NumberCompareTextField currentValue={fieldValue} diffingValue={diffingValue} prefix='$' />
      ) : (
        <FormattedNumber value={fieldValue} prefix='$' fixedDecimalScale decimalScale={2} />
      );
    case 'percent':
      return (editMode && isRejected) || editMode ? (
        <NumberTextField
          name={name}
          label={label}
          percent
          decimalScale={3}
          error={Boolean(error)}
          helperText={helperText}
          rules={rules}
        />
      ) : (diffingMode || isDraft) && !isRejected ? (
        <NumberCompareTextField currentValue={fieldValue} diffingValue={diffingValue} percent decimalScale={3} />
      ) : (
        <FormattedNumber value={Number(fieldValue) * 100} suffix='%' fixedDecimalScale decimalScale={3} />
      );
    default:
      return (editMode && isRejected) || editMode ? (
        <NumberTextField
          name={name}
          label={label}
          error={Boolean(error)}
          helperText={helperText}
          rules={rules}
          decimalScale={0}
        />
      ) : (diffingMode || isDraft) && !isRejected ? (
        <NumberCompareTextField currentValue={fieldValue} diffingValue={diffingValue} decimalScale={0} />
      ) : (
        <FormattedNumber value={fieldValue} decimalScale={0} />
      );
  }
}
